import React, { memo } from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-bootstrap';
import {
    Checkbox,
    FormControlLabel,
    Radio,
    Switch
} from '@sumologic/yama-ds-components';

import { isValid } from 'date-fns';

import {
    CALC,
    CHECKBOX,
    COLOR,
    DATE,
    DYNAMIC,
    EMAIL,
    LIST,
    MULTILIST,
    NUMERIC,
    PASSWORD,
    RADIO,
    TAGS,
    TEXT,
    TEXTAREA,
    TIME,
    TIMEINTERVAL,
    TIMEZONE,
    USER
} from '~/modules/customFieldTypes';

import {
    fromPHPToDateFnsFormat,
    getDateUnitTranslationObj,
    longDateFormat,
    longDateTimeFormat,
    unixTimestampToDate
} from '~/modules/helpers/date';
import { RE } from '~/modules/helpers/regexp';
import { isObject } from '~/modules/helpers/object';

import { isNumber } from '~/modules/helpers/math';

import Tag from '~/components/Tags/Tags';
import Select from '~/components/Select/Select';
import Editor2 from '~/components/Editor2/Editor2';
import Editable from '~/components/Editable/Editable';
import Translate from '~/components/Translate/Translate';
import ButtonBoxed from '~/components/ButtonBoxed/ButtonBoxed';
import ColorPicker from '~/components/ColorPicker/ColorPicker';
import PortalDatePicker from '~/components/DatePicker/PortalDatePicker';
import TimeIntervalInput from '~/components/TimeIntervalInput/TimeIntervalInput';

const FormField = ({
    id,
    onClick,
    onChange,
    type = TEXT,
    name,
    value,
    readOnly,
    isTouched,
    errors,
    options,
    acceptNewOptions,
    isRequired,
    isSwitch,
    isButton,
    isLabel,
    isValueEditable,
    allowAddValues,
    tooltip,
    inlineEdit,
    rawComponent,
    extraFormControlAttributes = {},
    isMulti = false,
    onTextAreaReady,
    ...otherProps
}) => {
    const isInvalid = isTouched && !!errors;

    let component;
    let componentProps = {
        name: name || id,
        id: id,
        value: value,
        autoComplete: 'off',
        readOnly: readOnly,
        disabled: readOnly, // todo: revise inline props and spread props precedence/repetition
        isInvalid: isInvalid,
        onChange: onChange,
        ...otherProps
    };

    const classes = [];
    if (isInvalid) {
        classes.push('is-invalid');
    }

    if (isLabel) {
        componentProps.isDisabled = false;
        componentProps.disabled = false;
    }

    const useEditable = inlineEdit && !!Object.keys(inlineEdit).length;

    switch (type) {
        case LIST:
        case MULTILIST:
        case TIMEZONE:
        case DYNAMIC:
            componentProps.className = classes;
            componentProps.isClearable = !isRequired;
            componentProps.isMulti = isMulti || MULTILIST === type;
            componentProps.isDisabled = componentProps.disabled;
            componentProps.enableNewOptions = acceptNewOptions;
            componentProps.createOptionPosition = 'first';
            componentProps.options = options;
            componentProps.formatCreateLabel = inputValue => (
                <>
                    <Translate message="Add manual value:" />
                    <span className="fw-bold ms-1">{inputValue}</span>
                </>
            );
            componentProps.autoFocus = useEditable;
            componentProps.onChange = value => {
                onChange(id, value);
            };
            componentProps.inputId = id;
            delete componentProps.id;
            component = <Select {...componentProps} />;
            break;
        case TAGS:
            componentProps.className = classes.join('\u0020');
            componentProps.isClearable = true;
            componentProps.isValueEditable = isValueEditable;
            componentProps.allowAddValues = allowAddValues;
            if (value && 'string' === typeof value) {
                componentProps.value = value
                    .split(',') // todo: consider leading spaces? ',\s*' ...possibly define a global rule for tag separators
                    .map(tag => {
                        return {
                            value: tag,
                            label: tag
                        };
                    });
            } else if (value?.[0]) {
                componentProps.value = value.map(tag => {
                    return tag
                        ? 'object' !== typeof tag
                            ? {
                                  value: tag,
                                  label: tag
                              }
                            : tag
                        : '';
                });
            }
            componentProps.onChange = value => onChange(id, value);
            component = <Tag {...componentProps} />;
            break;
        case CHECKBOX:
            if (isButton) {
                componentProps.onClick = event => onChange(id, !value, event);
                component = (
                    <ButtonBoxed
                        {...{
                            icon: otherProps.icon,
                            disabled: readOnly,
                            tooltip: tooltip,
                            active: value,
                            ...componentProps
                        }}
                    />
                );
            } else {
                componentProps.checked = !!value;
                componentProps.onChange = event =>
                    onChange(id, event.target.checked, event);
                delete componentProps.hashType;
                delete componentProps.internalName;

                const ControlComponent = isSwitch ? Switch : Checkbox;

                // Extract props to pass through inputProps for form handling and testing
                // Only pass valid DOM attributes (no custom props like 'hint')
                const inputPropsToForward = {
                    input: {
                        id: componentProps.id,
                        name: componentProps.name,
                        value: componentProps.value,
                        'data-testid': otherProps['data-testid'],
                        // Map hint to title for native browser tooltip if provided
                        ...(otherProps.hint && { title: otherProps.hint })
                    }
                };

                const formControlLabel = (
                    <FormControlLabel
                        control={
                            <ControlComponent
                                {...{
                                    disabled: readOnly,
                                    checked: componentProps.checked,
                                    onChange: componentProps.onChange,
                                    slotProps: inputPropsToForward
                                }}
                            />
                        }
                        label={otherProps.label || ''}
                    />
                );

                // Only wrap Switch with left margin for alignment, keep Checkbox unwrapped
                component = isSwitch ? (
                    <div className="mui-ms-2-5">{formControlLabel}</div>
                ) : (
                    formControlLabel
                );
            }
            break;
        case DATE:
        case TIME: {
            classes.push('form-control');
            componentProps.className = classes.join('\u0020');
            componentProps.isClearable = !!componentProps.value;
            componentProps.placeholderText = 'Click to select a date';
            componentProps.showMonthDropdown = true;
            componentProps.showYearDropdown = true;
            const isTimeType = TIME === type;
            componentProps.showTimeInput = isTimeType;

            const dateFormatKey = isTimeType
                ? longDateTimeFormat
                : longDateFormat;

            const settingsDateFormat = SETTINGS.dateFormats[dateFormatKey];

            componentProps.dateFormat =
                fromPHPToDateFnsFormat(settingsDateFormat);

            // console.log({ dateFormatKey, settingsDateFormat, dateFormat: componentProps.dateFormat });

            componentProps.onChange = (val, e) => {
                onChange(id, val || '', e);
            };

            let selectedDate = null;
            if (isValid(value)) {
                selectedDate = isNumber(value)
                    ? unixTimestampToDate(value)
                    : value;
            } else {
                selectedDate = new Date();
                componentProps.value = '';
            }
            componentProps.selected = selectedDate;
            componentProps.type = type;
            component = (
                <PortalDatePicker
                    {...{
                        disabled: readOnly,
                        ...componentProps
                    }}
                />
            );
            break;
        }
        case COLOR: {
            let color = value;
            if ('object' !== typeof color) {
                const match = value.match(RE.COLOR.rgbaValues);
                color = match
                    ? {
                          r: match[1],
                          g: match[2],
                          b: match[3]
                      }
                    : {
                          r: 0,
                          g: 0,
                          b: 0
                      };
            }
            componentProps.value = color;
            componentProps.onChange = e => onChange(id, e.rgb, e);
            component = <ColorPicker {...componentProps} />;
            break;
        }
        case CALC: {
            component = (
                <p className="calculation-field">
                    {isObject(value) ? value.value : value}
                </p>
            );
            break;
        }
        case TIMEINTERVAL: {
            const unit = ['y', 'm', 'd', 'h', 'i', 's'];
            const mappedUnit = unit.map(unit => {
                return {
                    value: unit,
                    label: <Translate {...getDateUnitTranslationObj(unit)} />
                };
            });
            component = (
                <TimeIntervalInput
                    {...{
                        ...componentProps,
                        unit: mappedUnit,
                        field: {
                            internalName: id,
                            value: value
                        }
                    }}
                />
            );
            break;
        }
        case TEXTAREA: {
            const editorClasses = 'h-100 toolbar-bg-light';
            let className = componentProps.className || [];
            if (className.push) {
                className.push(editorClasses);
            } else {
                className = className
                    ? `${className} ${editorClasses}`
                    : editorClasses;
            }
            componentProps.className = className; // handled by editor
            componentProps.data = value;
            componentProps.onChange = (event, cke) => {
                const actualData = cke.getData();
                return onChange(id, actualData);
            };
            componentProps.onBlur = null;

            componentProps.onReady = cke => {
                onTextAreaReady?.(cke, id);
            };

            component = (
                <Editor2
                    {...{
                        disabled: readOnly,
                        ...componentProps
                    }}
                />
            );
            break;
        }
        case RADIO: {
            const { id, name, label } = componentProps;
            component = (
                <FormControlLabel
                    control={
                        <Radio
                            name={name}
                            value={id}
                            onChange={() => onChange(name, id)}
                            checked={id === value}
                            disabled={readOnly}
                            slotProps={{ input: { id: id } }}
                        />
                    }
                    label={label || ''}
                />
            );
            break;
        }
        case TEXT:
        case NUMERIC:
        case EMAIL:
        case USER:
        case PASSWORD:
        default:
            const htmlTypeMap = {
                [TEXT]: 'text',
                [NUMERIC]: 'number',
                [EMAIL]: 'email',
                [USER]: 'text',
                [PASSWORD]: 'password'
            };
            componentProps.type = htmlTypeMap[type];
            componentProps.className = classes.join('\u0020');
            const isNumericType = NUMERIC === type;
            componentProps.onChange = event => {
                let val = event.currentTarget.value;
                if (isNumericType) {
                    val = '' !== val ? Number(val) : '';
                }
                return onChange(id, val, event);
            };
            if (isNumericType) {
                componentProps = {
                    ...componentProps,
                    ...extraFormControlAttributes,
                    onInput: event => {
                        const el = event.target;
                        const elMin = el.min;
                        let val = el.value;
                        if (elMin && parseInt(elMin) >= 0) {
                            val = el.value = val.replace(RE.notDigit, '');
                        }
                        const extraMin = extraFormControlAttributes?.min;
                        if (extraMin && val < extraMin) {
                            val = el.value = extraMin;
                        }
                        const extraMax = extraFormControlAttributes?.max;
                        if (extraMax && val > extraMax) {
                            el.value = extraMax;
                        }
                    }
                };
            } else {
                if (
                    TEXT === type &&
                    componentProps.hasOwnProperty('onCreateOption')
                ) {
                    delete componentProps.onCreateOption;
                }
            }
            // mm: value is required to maintain the component as controlled
            if ('undefined' === typeof componentProps.value) {
                componentProps.value = '';
            }
            if (isLabel) {
                componentProps.onClick = onClick;
            }
            delete componentProps.hashType;
            delete componentProps.internalName;
            component = <Form.Control {...componentProps} />;
    }

    return useEditable ? (
        <Editable
            id={id}
            keyId={id}
            type={type}
            isEditable={readOnly ? false : inlineEdit.editable || false}
            setEditable={inlineEdit.setEditable}
            rawComponent={rawComponent}
        >
            {component}
        </Editable>
    ) : (
        component
    );
};

// prettier-ignore
FormField.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    label: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.string
    ]).isRequired,
    errors: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.string
    ]),
    onChange: PropTypes.func.isRequired,
    type: PropTypes.number,
    name: PropTypes.string,
    value: PropTypes.any,
    extraFormControlAttributes: PropTypes.object,
    rawComponent: PropTypes.bool, //rn: used if needed for Editable component if inlineEdit is enabled
    readOnly: PropTypes.bool,
    isRequired: PropTypes.bool,
    isTouched: PropTypes.bool,
    isSwitch: PropTypes.bool, // 𝘴𝘩𝘰𝘸 𝘤𝘩𝘦𝘤𝘬𝘣𝘰𝘹 𝘧𝘪𝘦𝘭𝘥 𝘢𝘴 𝘴𝘸𝘪𝘵𝘤𝘩 𝘤𝘰𝘮𝘱𝘰𝘯𝘦𝘯𝘵
    isButton: PropTypes.bool,
    isValueEditable: PropTypes.bool,
    allowAddValues: PropTypes.bool,
    acceptNewOptions: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.any
        })
    ),
    inlineEdit: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            editable: PropTypes.bool,
            setEditable: PropTypes.func
        })
    ])
};

// FormField.defaultProps = {};

export default memo(FormField);
